import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Dimensions,
  Platform,
  ActivityIndicator,
  Pressable,
  Linking,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useIconTheme } from '../contexts/IconThemeContext';
import { getHotspotById, getHotspotReviews, getNearbyUsers, getRecurringEvents } from '../services/database';
import { getRecentSocialPosts } from '../services/socialSyncService';
import LoadingAnimation from '../components/LoadingAnimation';
import WriteReviewModal from '../components/WriteReviewModal';
import { getProfilePictureUrl, getHotspotPhotoUrl } from '../services/storage';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isDesktop = Platform.OS === 'web' && SCREEN_WIDTH >= 768;

const HotspotDetailScreen = ({ route, navigation }) => {
  const { hotspotId, hotspot: initialHotspot, clubId: routeClubId } = route.params;
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const { getCategoryIcon } = useIconTheme();
  
  const [hotspot, setHotspot] = useState(initialHotspot || null);
  const [loading, setLoading] = useState(!initialHotspot);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [avgRating, setAvgRating] = useState(0);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [windowDimensions, setWindowDimensions] = useState({ width: SCREEN_WIDTH, height: SCREEN_HEIGHT });
  const [recentSocialPosts, setRecentSocialPosts] = useState([]);
  const [socialPostsPlatform, setSocialPostsPlatform] = useState(undefined);
  const [loadingSocial, setLoadingSocial] = useState(false);
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [loadingRecurring, setLoadingRecurring] = useState(false);
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleResize = () => {
        setWindowDimensions({
          width: Dimensions.get('window').width,
          height: Dimensions.get('window').height,
        });
      };
      Dimensions.addEventListener('change', handleResize);
      return () => {
        // Cleanup if needed
      };
    }
  }, []);

  const isDesktopView = Platform.OS === 'web' && windowDimensions.width >= 768;

  useEffect(() => {
    // If we weren't given a full hotspot object, fetch it by id.
    // For map flows we already have enough hotspot data passed in;
    // social posts now rely primarily on clubId/claimed_by instead
    // of the owner join, so this fetch is best-effort.
    if (!initialHotspot && hotspotId) {
      fetchHotspotDetails();
    }
    fetchCurrentUserDbId();
    fetchReviews();
    fetchNearbyPlayers();
  }, [hotspotId]);

  useEffect(() => {
    // Club user that owns this venue. Prefer explicit clubId from route when present,
    // then fall back to hotspot owner / claimed_by fields (both DB + client shapes).
    const ownerUserId =
      routeClubId ||
      (hotspot && hotspot.owner && hotspot.owner.id) ||
      hotspot?.claimed_by ||
      hotspot?.claimedBy;

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'HotspotDetailScreen.js:socialEffect',message:'social effect inputs',data:{routeClubId,hotspotId:hotspot?.id,claimed_by:hotspot?.claimed_by,claimedBy:hotspot?.claimedBy,ownerId:hotspot?.owner?.id,ownerUserId},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
    // #endregion

    if (!ownerUserId) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'HotspotDetailScreen.js:socialEffect',message:'no ownerUserId, skipping fetch',data:{routeClubId},timestamp:Date.now(),hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      setRecentSocialPosts([]);
      setSocialPostsPlatform(undefined);
      return;
    }

    const fetchSocial = async () => {
      try {
        setLoadingSocial(true);
        const { posts, platform } = await getRecentSocialPosts(ownerUserId, 6);
        setRecentSocialPosts(posts || []);
        setSocialPostsPlatform(platform);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0bf1ae'},body:JSON.stringify({sessionId:'0bf1ae',location:'HotspotDetailScreen.js:fetchSocial',message:'getRecentSocialPosts result',data:{ownerUserId,postsLength:(posts||[]).length},timestamp:Date.now(),hypothesisId:'D'})}).catch(()=>{});
        // #endregion
      } catch (e) {
        console.log('No social posts for hotspot/club or error:', e.message);
        setRecentSocialPosts([]);
        setSocialPostsPlatform(undefined);
      } finally {
        setLoadingSocial(false);
      }
    };

    fetchSocial();
  }, [routeClubId, hotspot?.owner?.id, hotspot?.claimed_by, hotspot?.claimedBy]);

  // Fetch recurring schedules when hotspot is claimed by a club
  useEffect(() => {
    const ownerUserId =
      routeClubId ||
      (hotspot && hotspot.owner && hotspot.owner.id) ||
      hotspot?.claimed_by ||
      hotspot?.claimedBy;
    if (!ownerUserId) {
      setRecurringSchedules([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingRecurring(true);
      try {
        const data = await getRecurringEvents(ownerUserId);
        if (!cancelled) setRecurringSchedules((data || []).filter(s => s.is_active !== false));
      } catch (e) {
        if (!cancelled) setRecurringSchedules([]);
      } finally {
        if (!cancelled) setLoadingRecurring(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [routeClubId, hotspot?.owner?.id, hotspot?.claimed_by, hotspot?.claimedBy]);

  const fetchHotspotDetails = async () => {
    try {
      setLoading(true);
      const data = await getHotspotById(hotspotId);
      setHotspot(data);
    } catch (error) {
      console.error('Error fetching hotspot details:', error);
      showToast('Failed to load hotspot details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserDbId = async () => {
    if (appUser?.id) {
      setCurrentUserDbId(appUser.id);
    }
  };

  const fetchReviews = async () => {
    const targetId = hotspotId || initialHotspot?.id;
    if (!targetId) return;
    
    try {
      setLoadingReviews(true);
      
      const fetchedReviews = await getHotspotReviews(targetId, 20);
      
      const reviewsWithUrls = fetchedReviews.map((review) => {
        if (review.user?.profilePictureUrl && !review.user.profilePictureUrl.startsWith('http')) {
          const url = getProfilePictureUrl(review.user.profilePictureUrl);
          return { ...review, user: { ...review.user, profilePictureUrl: url } };
        }
        return review;
      });
      
      setReviews(reviewsWithUrls);
      
      if (reviewsWithUrls.length > 0) {
        const avg = reviewsWithUrls.reduce((sum, r) => sum + r.rating, 0) / reviewsWithUrls.length;
        setAvgRating(avg);
      } else {
        setAvgRating(0);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
      setAvgRating(0);
    } finally {
      setLoadingReviews(false);
    }
  };

  const fetchNearbyPlayers = async () => {
    const targetHotspot = hotspot || initialHotspot;
    if (!targetHotspot?.latitude || !targetHotspot?.longitude) return;
    
    try {
      setLoadingPlayers(true);
      const allUsers = await getNearbyUsers(targetHotspot.latitude, targetHotspot.longitude, 0.5);
      
      const nearby = (allUsers || []).filter(u => {
        if (!u.latitude || !u.longitude || u.account_type === 'CLUB') return false;
        return true;
      });
      
      const playersWithUrls = nearby.slice(0, 10).map((player) => {
        const picUrl = player.profile_picture_url;
        if (picUrl && !picUrl.startsWith('http')) {
          return { ...player, profilePictureUrl: getProfilePictureUrl(picUrl), username: player.username };
        }
        return { ...player, profilePictureUrl: picUrl, username: player.username };
      });
      
      setNearbyPlayers(playersWithUrls);
    } catch (error) {
      console.error('Error fetching nearby players:', error);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      PARK: 'Park',
      CAFE: 'Cafe',
      BAR: 'Bar',
      CLUB: 'Chess Club',
      UNIVERSITY: 'University',
      PUBLIC_SQUARE: 'Public Square',
      LIBRARY: 'Library',
      OTHER: 'Other',
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      PARK: '#4CAF50',
      CAFE: '#FF9800',
      BAR: '#9C27B0',
      CLUB: '#2196F3',
      UNIVERSITY: '#F44336',
      PUBLIC_SQUARE: '#00BCD4',
      LIBRARY: '#795548',
      OTHER: '#607D8B',
    };
    return colors[category] || '#607D8B';
  };

  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const formatTime12 = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  const formatRecurringLine = (s) =>
    `Every ${DAYS[s.day_of_week] || 'Day'} ${formatTime12(s.start_time)}${s.end_time ? ` – ${formatTime12(s.end_time)}` : ''}${s.title ? ` (${s.title})` : ''}`;
  const hasScheduleContent = hotspot?.schedule || (recurringSchedules && recurringSchedules.length > 0);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon
          key={i}
          name={i <= rating ? 'star' : i - 0.5 <= rating ? 'star-half' : 'star-border'}
          size={16}
          color="#FFD700"
        />
      );
    }
    return stars;
  };

  const handleReviewSubmitted = async (newReview) => {
    let reviewWithUrl = newReview;
    if (newReview.user?.profilePictureUrl && !newReview.user.profilePictureUrl.startsWith('http')) {
      const url = getProfilePictureUrl(newReview.user.profilePictureUrl);
      reviewWithUrl = { ...newReview, user: { ...newReview.user, profilePictureUrl: url } };
    }
    
    const updatedReviews = [reviewWithUrl, ...reviews];
    setReviews(updatedReviews);
    
    const avg = updatedReviews.reduce((sum, r) => sum + r.rating, 0) / updatedReviews.length;
    setAvgRating(avg);
    
    showToast('Review submitted successfully!', 'success');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, isDesktopView && styles.desktopBackdrop]}>
        <View style={isDesktopView ? styles.desktopLoadingCard : undefined}>
          <LoadingAnimation size={isDesktopView ? 300 : 525} />
        </View>
      </View>
    );
  }

  if (!hotspot) {
    return (
      <View style={[styles.container, isDesktopView && styles.desktopBackdrop]}>
        <View style={[styles.errorContainer, isDesktopView && styles.desktopErrorCard]}>
          <Icon name="error-outline" size={64} color="#ccc" />
          <Text style={styles.errorText}>Hotspot not found</Text>
          <TouchableOpacity style={styles.backButtonError} onPress={() => navigation.goBack()}>
            <Text style={styles.backButtonErrorText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const rawPhotos = hotspot.photos || [];
  const photos = rawPhotos.map((p) => (p && String(p).startsWith('http') ? p : getHotspotPhotoUrl(p))).filter(Boolean);

  // Desktop Layout - Modal/Popup Style
  if (isDesktopView) {
    return (
      <Pressable style={styles.desktopBackdrop} onPress={() => navigation.goBack()}>
        <Pressable style={styles.desktopModal} onPress={(e) => e.stopPropagation()}>
          {/* Close Button */}
          <TouchableOpacity style={styles.desktopCloseButton} onPress={() => navigation.goBack()}>
            <Icon name="close" size={24} color="#666" />
          </TouchableOpacity>

          <ScrollView
            style={styles.desktopScrollView}
            contentContainerStyle={{ paddingBottom: 32 }}
            showsVerticalScrollIndicator={true}
          >
            {/* Two Column Header */}
            <View style={styles.desktopHeader}>
              {/* Left: Photo */}
              <View style={styles.desktopPhotoSection}>
                {photos.length > 0 ? (
                  <View style={styles.desktopPhotoContainer}>
                    <Image source={{ uri: photos[activePhotoIndex] }} style={styles.desktopPhoto} />
                    {photos.length > 1 && (
                      <View style={styles.desktopPhotoNav}>
                        <TouchableOpacity
                          style={[styles.desktopPhotoNavBtn, activePhotoIndex === 0 && styles.desktopPhotoNavBtnDisabled]}
                          onPress={() => activePhotoIndex > 0 && setActivePhotoIndex(activePhotoIndex - 1)}
                          disabled={activePhotoIndex === 0}
                        >
                          <Icon name="chevron-left" size={20} color={activePhotoIndex === 0 ? '#ccc' : '#fff'} />
                        </TouchableOpacity>
                        <Text style={styles.desktopPhotoCounter}>{activePhotoIndex + 1} / {photos.length}</Text>
                        <TouchableOpacity
                          style={[styles.desktopPhotoNavBtn, activePhotoIndex === photos.length - 1 && styles.desktopPhotoNavBtnDisabled]}
                          onPress={() => activePhotoIndex < photos.length - 1 && setActivePhotoIndex(activePhotoIndex + 1)}
                          disabled={activePhotoIndex === photos.length - 1}
                        >
                          <Icon name="chevron-right" size={20} color={activePhotoIndex === photos.length - 1 ? '#ccc' : '#fff'} />
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                ) : (
                  <View style={styles.desktopPhotoPlaceholder}>
                    <Text style={styles.desktopPhotoPlaceholderIcon}>{getCategoryIcon(hotspot.category)}</Text>
                    <Text style={styles.desktopPhotoPlaceholderText}>No photos</Text>
                  </View>
                )}
              </View>

              {/* Right: Info */}
              <View style={styles.desktopInfoSection}>
                {/* Category and Tags Row */}
                <View style={styles.desktopTagsRow}>
                  <View style={[styles.desktopCategoryBadge, { backgroundColor: getCategoryColor(hotspot.category) }]}>
                    <Text style={styles.desktopCategoryText}>{getCategoryLabel(hotspot.category)}</Text>
                  </View>
                  {hotspot.tags && hotspot.tags.length > 0 && hotspot.tags.map((tag, index) => (
                    <View key={index} style={styles.desktopTagBadge}>
                      <Text style={styles.desktopTagText}>
                        {tag.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Text>
                    </View>
                  ))}
                </View>
                
                <View style={styles.desktopTitleRow}>
                  <Text style={styles.desktopHotspotIcon}>{getCategoryIcon(hotspot.category)}</Text>
                  <Text style={styles.desktopHotspotName}>{hotspot.name}</Text>
                </View>

                {/* Rating */}
                <View style={styles.desktopRating}>
                  <View style={styles.starsRow}>{renderStars(Math.round(avgRating))}</View>
                  <Text style={styles.desktopRatingText}>
                    {reviews.length > 0 
                      ? `${avgRating.toFixed(1)} (${reviews.length} ${reviews.length === 1 ? 'review' : 'reviews'})`
                      : 'No reviews yet'}
                  </Text>
                </View>

                {/* Location */}
                {hotspot.city && (
                  <View style={styles.desktopLocationRow}>
                    <Icon name="location-city" size={16} color="#666" />
                    <Text style={styles.desktopCityText}>{hotspot.city}</Text>
                  </View>
                )}
                {hotspot.address && (
                  <View style={styles.desktopAddressRow}>
                    <Icon name="place" size={16} color="#94EB98" />
                    <Text style={styles.desktopAddressText}>{hotspot.address}</Text>
                  </View>
                )}

                {/* Activity Level */}
                {hotspot.activityLevel && (
                  <View style={styles.desktopActivityBadge}>
                    <Icon name="trending-up" size={14} color="#FF9800" />
                    <Text style={styles.desktopActivityText}>{hotspot.activityLevel}</Text>
                  </View>
                )}

                {/* Social Links */}
                {(hotspot.instagram_url || hotspot.twitter_url || hotspot.facebook_url || hotspot.website_url) && (
                  <View style={styles.desktopSocialLinksRow}>
                    {hotspot.instagram_url && (
                      <TouchableOpacity 
                        style={styles.desktopSocialLink}
                        onPress={() => Platform.OS === 'web' ? window.open(hotspot.instagram_url, '_blank') : null}
                      >
                        <Icon name="camera-alt" size={18} color="#E4405F" />
                      </TouchableOpacity>
                    )}
                    {hotspot.twitter_url && (
                      <TouchableOpacity 
                        style={styles.desktopSocialLink}
                        onPress={() => Platform.OS === 'web' ? window.open(hotspot.twitter_url, '_blank') : null}
                      >
                        <Icon name="chat" size={18} color="#1DA1F2" />
                      </TouchableOpacity>
                    )}
                    {hotspot.facebook_url && (
                      <TouchableOpacity 
                        style={styles.desktopSocialLink}
                        onPress={() => Platform.OS === 'web' ? window.open(hotspot.facebook_url, '_blank') : null}
                      >
                        <Icon name="thumb-up" size={18} color="#1877F2" />
                      </TouchableOpacity>
                    )}
                    {hotspot.website_url && (
                      <TouchableOpacity 
                        style={styles.desktopSocialLinkWithUrl}
                        onPress={() => Platform.OS === 'web' ? window.open(hotspot.website_url, '_blank') : Linking.openURL(hotspot.website_url)}
                        activeOpacity={0.7}
                      >
                        <Icon name="language" size={18} color="#4CAF50" />
                        <Text style={styles.desktopWebsiteUrlText} numberOfLines={1}>{hotspot.website_url}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            </View>

            {/* Description */}
            <View style={styles.desktopSection}>
              <Text style={styles.desktopSectionTitle}>About</Text>
              <Text style={styles.desktopDescription}>
                {hotspot.description || 'No description available.'}
              </Text>
            </View>

            {/* Recent Social Activity (club posts) */}
            {recentSocialPosts.length > 0 && (
              <View style={styles.desktopSection}>
                <Text style={styles.desktopSectionTitle}>
                  {socialPostsPlatform === 'instagram'
                    ? 'Recent Posts from Instagram'
                    : socialPostsPlatform === 'facebook'
                      ? 'Recent Posts from Facebook'
                      : 'Recent Posts'}
                </Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 10, paddingVertical: 4 }}
                >
                  {recentSocialPosts.map((post) => {
                    let payload = post.raw_payload || {};
                    if (typeof payload === 'string') {
                      try {
                        payload = JSON.parse(payload);
                      } catch {
                        payload = {};
                      }
                    }
                    const imageUrl =
                      (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                      post.media_url;
                    return (
                      <TouchableOpacity
                        key={post.id}
                        style={styles.desktopSocialPostCard}
                        activeOpacity={0.8}
                        onPress={() => post.permalink && (Platform.OS === 'web' ? window.open(post.permalink, '_blank') : Linking.openURL(post.permalink))}
                      >
                        {imageUrl ? (
                          <Image source={{ uri: imageUrl }} style={styles.desktopSocialPostImage} />
                        ) : (
                          <View style={styles.desktopSocialPostPlaceholder}>
                            <Icon name="image" size={24} color="#999" />
                          </View>
                        )}
                        {post.caption ? (
                          <Text style={styles.desktopSocialPostCaption} numberOfLines={2}>{post.caption}</Text>
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            )}

            {/* Two Column: Schedule + Amenities */}
            <View style={styles.desktopTwoColumn}>
              {/* Schedule */}
              <View style={styles.desktopColumnHalf}>
                <Text style={styles.desktopSectionTitle}>Schedule</Text>
                {loadingRecurring ? (
                  <View style={styles.desktopEmptyState}>
                    <ActivityIndicator size="small" color="#94EB98" />
                  </View>
                ) : hasScheduleContent ? (
                  <View style={styles.desktopScheduleCard}>
                    <Icon name="schedule" size={20} color="#94EB98" />
                    {hotspot.schedule ? (
                      <Text style={styles.desktopScheduleText}>{hotspot.schedule}</Text>
                    ) : null}
                    {recurringSchedules.length > 0 && recurringSchedules.map((s, idx) => (
                      <Text key={s.id || idx} style={[styles.desktopScheduleText, idx > 0 || hotspot.schedule ? { marginTop: 6 } : null]}>
                        {formatRecurringLine(s)}
                      </Text>
                    ))}
                  </View>
                ) : (
                  <View style={styles.desktopEmptyState}>
                    <Icon name="event-busy" size={32} color="#ddd" />
                    <Text style={styles.desktopEmptyTitle}>No schedule posted</Text>
                    <Text style={styles.desktopEmptyText}>Check back later</Text>
                  </View>
                )}
              </View>

              {/* Amenities */}
              <View style={styles.desktopColumnHalf}>
                <Text style={styles.desktopSectionTitle}>Amenities</Text>
                {hotspot.amenities && hotspot.amenities.length > 0 ? (
                  <View style={styles.desktopAmenitiesGrid}>
                    {hotspot.amenities.map((amenity, index) => (
                      <View key={index} style={styles.desktopAmenityTag}>
                        <Icon name="check-circle" size={14} color="#94EB98" />
                        <Text style={styles.desktopAmenityText}>{amenity}</Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.desktopEmptyState}>
                    <Icon name="info-outline" size={32} color="#ddd" />
                    <Text style={styles.desktopEmptyTitle}>No amenities listed</Text>
                    <Text style={styles.desktopEmptyText}>None added yet</Text>
                  </View>
                )}
              </View>
            </View>

            {/* Players Nearby */}
            <View style={styles.desktopSection}>
              <Text style={styles.desktopSectionTitle}>Players Nearby</Text>
              {loadingPlayers ? (
                <ActivityIndicator size="small" color="#94EB98" />
              ) : nearbyPlayers.length > 0 ? (
                <View style={styles.desktopPlayersGrid}>
                  {nearbyPlayers.map((player) => (
                    <TouchableOpacity 
                      key={player.id} 
                      style={styles.desktopPlayerCard}
                      onPress={() => navigation.navigate('UserDetail', { userId: player.id })}
                    >
                      {player.profilePictureUrl ? (
                        <Image source={{ uri: player.profilePictureUrl }} style={styles.desktopPlayerAvatar} />
                      ) : (
                        <View style={styles.desktopPlayerAvatarPlaceholder}>
                          <Icon name="person" size={16} color="#94EB98" />
                        </View>
                      )}
                      <Text style={styles.desktopPlayerName} numberOfLines={1}>{player.username}</Text>
                      {player.isOnline && <View style={styles.desktopOnlineDot} />}
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                <View style={styles.desktopEmptyHighlight}>
                  <View style={styles.desktopEmptyIconCircle}>
                    <Icon name="group-add" size={28} color="#94EB98" />
                  </View>
                  <Text style={styles.desktopEmptyHighlightTitle}>Be the first to visit!</Text>
                  <Text style={styles.desktopEmptyHighlightText}>
                    Head here to show up on the map
                  </Text>
                </View>
              )}
            </View>

            {/* Reviews */}
            <View style={styles.desktopSection}>
              <View style={styles.desktopSectionHeader}>
                <Text style={styles.desktopSectionTitle}>Reviews</Text>
                {currentUserDbId && (
                  <TouchableOpacity 
                    style={styles.desktopWriteReviewBtn}
                    onPress={() => setShowWriteReview(true)}
                  >
                    <Icon name="rate-review" size={16} color="#fff" />
                    <Text style={styles.desktopWriteReviewText}>Write Review</Text>
                  </TouchableOpacity>
                )}
              </View>
              
              {loadingReviews ? (
                <ActivityIndicator size="small" color="#94EB98" />
              ) : reviews.length > 0 ? (
                <View style={styles.desktopReviewsGrid}>
                  {reviews.map((review) => (
                    <View key={review.id} style={styles.desktopReviewCard}>
                      <View style={styles.desktopReviewHeader}>
                        <TouchableOpacity 
                          style={styles.desktopReviewerInfo}
                          onPress={() => review.user?.id && navigation.navigate('UserDetail', { userId: review.user.id })}
                        >
                          {review.user?.profilePictureUrl ? (
                            <Image source={{ uri: review.user.profilePictureUrl }} style={styles.desktopReviewerAvatar} />
                          ) : (
                            <View style={styles.desktopReviewerAvatarPlaceholder}>
                              <Icon name="person" size={14} color="#94EB98" />
                            </View>
                          )}
                          <View>
                            <Text style={styles.desktopReviewerName}>{review.user?.username || 'Anonymous'}</Text>
                            <Text style={styles.desktopReviewDate}>{formatDate(review.createdAt)}</Text>
                          </View>
                        </TouchableOpacity>
                        <View style={styles.starsRow}>{renderStars(review.rating)}</View>
                      </View>
                      {review.comment && (
                        <Text style={styles.desktopReviewComment}>{review.comment}</Text>
                      )}
                    </View>
                  ))}
                </View>
              ) : (
                <View style={styles.desktopEmptyHighlight}>
                  <View style={[styles.desktopEmptyIconCircle, { backgroundColor: '#FFF8E1' }]}>
                    <Icon name="star-outline" size={28} color="#FFD700" />
                  </View>
                  <Text style={styles.desktopEmptyHighlightTitle}>Be the first to review!</Text>
                  <Text style={styles.desktopEmptyHighlightText}>
                    Share your experience at this spot
                  </Text>
                  {currentUserDbId && (
                    <TouchableOpacity 
                      style={styles.desktopEmptyButton}
                      onPress={() => setShowWriteReview(true)}
                    >
                      <Icon name="rate-review" size={16} color="#fff" />
                      <Text style={styles.desktopEmptyButtonText}>Write a Review</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>

            <View style={styles.desktopBottomSpacer} />
          </ScrollView>
        </Pressable>

        <WriteReviewModal
          visible={showWriteReview}
          onClose={() => setShowWriteReview(false)}
          hotspotId={hotspot.id}
          hotspotName={hotspot.name}
          userId={currentUserDbId}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </Pressable>
    );
  }

  // Mobile Layout - Full Screen
  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Hero Image Section */}
        <View style={styles.heroSection}>
          {photos.length > 0 ? (
            <>
              <Image source={{ uri: photos[activePhotoIndex] }} style={styles.heroImage} />
              {photos.length > 1 && (
                <View style={styles.photoIndicators}>
                  {photos.map((_, index) => (
                    <TouchableOpacity 
                      key={index} 
                      onPress={() => setActivePhotoIndex(index)}
                      style={[styles.photoIndicator, activePhotoIndex === index && styles.photoIndicatorActive]}
                    />
                  ))}
                </View>
              )}
              {photos.length > 1 && (
                <View style={styles.photoNavigation}>
                  <TouchableOpacity
                    style={[styles.photoNavButton, activePhotoIndex === 0 && styles.photoNavButtonDisabled]}
                    onPress={() => activePhotoIndex > 0 && setActivePhotoIndex(activePhotoIndex - 1)}
                    disabled={activePhotoIndex === 0}
                  >
                    <Icon name="chevron-left" size={28} color={activePhotoIndex === 0 ? '#ccc' : '#fff'} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.photoNavButton, activePhotoIndex === photos.length - 1 && styles.photoNavButtonDisabled]}
                    onPress={() => activePhotoIndex < photos.length - 1 && setActivePhotoIndex(activePhotoIndex + 1)}
                    disabled={activePhotoIndex === photos.length - 1}
                  >
                    <Icon name="chevron-right" size={28} color={activePhotoIndex === photos.length - 1 ? '#ccc' : '#fff'} />
                  </TouchableOpacity>
                </View>
              )}
            </>
          ) : (
            <View style={styles.heroPlaceholder}>
              <Text style={styles.heroPlaceholderIcon}>{getCategoryIcon(hotspot.category)}</Text>
              <Text style={styles.heroPlaceholderText}>No photos yet</Text>
            </View>
          )}
          
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Icon name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          
          <View style={[styles.categoryBadgeOverlay, { backgroundColor: getCategoryColor(hotspot.category) }]}>
            <Text style={styles.categoryBadgeText}>{getCategoryLabel(hotspot.category)}</Text>
          </View>
        </View>

        <View style={styles.content}>
          {/* Tags Row (if tags exist) */}
          {hotspot.tags && hotspot.tags.length > 0 && (
            <View style={styles.mobileTagsRow}>
              {hotspot.tags.map((tag, index) => (
                <View key={index} style={styles.mobileTagBadge}>
                  <Text style={styles.mobileTagText}>
                    {tag.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </Text>
                </View>
              ))}
            </View>
          )}

          <View style={styles.headerSection}>
            <View style={styles.titleRow}>
              <Text style={styles.hotspotIcon}>{getCategoryIcon(hotspot.category)}</Text>
              <Text style={styles.hotspotName}>{hotspot.name}</Text>
            </View>
            
            <View style={styles.ratingSummary}>
              <View style={styles.starsContainer}>{renderStars(Math.round(avgRating))}</View>
              <Text style={styles.ratingText}>
                {reviews.length > 0 
                  ? `${avgRating.toFixed(1)} (${reviews.length} ${reviews.length === 1 ? 'review' : 'reviews'})`
                  : 'No reviews yet'}
              </Text>
            </View>
            
            {hotspot.city && (
              <View style={styles.locationRow}>
                <Icon name="location-city" size={18} color="#666" />
                <Text style={styles.cityText}>{hotspot.city}</Text>
              </View>
            )}
            
            {hotspot.address && (
              <View style={styles.addressRow}>
                <Icon name="place" size={18} color="#94EB98" />
                <Text style={styles.addressText}>{hotspot.address}</Text>
              </View>
            )}

            {/* Social Links */}
            {(hotspot.instagram_url || hotspot.twitter_url || hotspot.facebook_url || hotspot.website_url) && (
              <View style={styles.mobileSocialLinksRow}>
                {hotspot.instagram_url && (
                  <TouchableOpacity 
                    style={styles.mobileSocialLink}
                    onPress={() => Platform.OS === 'web' ? window.open(hotspot.instagram_url, '_blank') : null}
                  >
                    <Icon name="camera-alt" size={20} color="#E4405F" />
                  </TouchableOpacity>
                )}
                {hotspot.twitter_url && (
                  <TouchableOpacity 
                    style={styles.mobileSocialLink}
                    onPress={() => Platform.OS === 'web' ? window.open(hotspot.twitter_url, '_blank') : null}
                  >
                    <Icon name="chat" size={20} color="#1DA1F2" />
                  </TouchableOpacity>
                )}
                {hotspot.facebook_url && (
                  <TouchableOpacity 
                    style={styles.mobileSocialLink}
                    onPress={() => Platform.OS === 'web' ? window.open(hotspot.facebook_url, '_blank') : null}
                  >
                    <Icon name="thumb-up" size={20} color="#1877F2" />
                  </TouchableOpacity>
                )}
                {hotspot.website_url && (
                  <TouchableOpacity 
                    style={styles.mobileSocialLinkWithUrl}
                    onPress={() => Platform.OS === 'web' ? window.open(hotspot.website_url, '_blank') : Linking.openURL(hotspot.website_url)}
                    activeOpacity={0.7}
                  >
                    <Icon name="language" size={20} color="#4CAF50" />
                    <Text style={styles.websiteUrlText} numberOfLines={1}>{hotspot.website_url}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>About</Text>
            <Text style={styles.description}>{hotspot.description || 'No description available.'}</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Schedule</Text>
            {loadingRecurring ? (
              <View style={styles.emptyState}>
                <ActivityIndicator size="small" color="#94EB98" />
              </View>
            ) : hasScheduleContent ? (
              <View style={styles.scheduleCard}>
                <Icon name="schedule" size={24} color="#94EB98" />
                {hotspot.schedule ? (
                  <Text style={styles.scheduleText}>{hotspot.schedule}</Text>
                ) : null}
                {recurringSchedules.length > 0 && recurringSchedules.map((s, idx) => (
                  <Text key={s.id || idx} style={[styles.scheduleText, (idx > 0 || hotspot.schedule) ? { marginTop: 8 } : null]}>
                    {formatRecurringLine(s)}
                  </Text>
                ))}
              </View>
            ) : (
              <View style={styles.emptyState}>
                <Icon name="event-busy" size={48} color="#ddd" />
                <Text style={styles.emptyStateTitle}>No schedule posted</Text>
                <Text style={styles.emptyStateText}>Check back later for regular hours</Text>
              </View>
            )}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Amenities</Text>
            {hotspot.amenities && hotspot.amenities.length > 0 ? (
              <View style={styles.amenitiesGrid}>
                {hotspot.amenities.map((amenity, index) => (
                  <View key={index} style={styles.amenityTag}>
                    <Icon name="check-circle" size={16} color="#94EB98" />
                    <Text style={styles.amenityText}>{amenity}</Text>
                  </View>
                ))}
              </View>
            ) : (
              <View style={styles.emptyState}>
                <Icon name="info-outline" size={48} color="#ddd" />
                <Text style={styles.emptyStateTitle}>No amenities listed</Text>
                <Text style={styles.emptyStateText}>This location hasn't added amenities yet</Text>
              </View>
            )}
          </View>

          {/* Recent Social Activity (club posts) */}
          {recentSocialPosts.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                {socialPostsPlatform === 'instagram'
                  ? 'Recent Posts from Instagram'
                  : socialPostsPlatform === 'facebook'
                    ? 'Recent Posts from Facebook'
                    : 'Recent Posts'}
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 10, paddingVertical: 4 }}
              >
                {recentSocialPosts.map((post) => {
                  let payload = post.raw_payload || {};
                  if (typeof payload === 'string') {
                    try {
                      payload = JSON.parse(payload);
                    } catch {
                      payload = {};
                    }
                  }
                  const imageUrl =
                    (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                    post.media_url;
                  return (
                    <TouchableOpacity
                      key={post.id}
                      style={styles.socialPostThumb}
                      activeOpacity={0.8}
                      onPress={() => post.permalink && (Platform.OS === 'web' ? window.open(post.permalink, '_blank') : Linking.openURL(post.permalink))}
                    >
                      {imageUrl ? (
                        <Image source={{ uri: imageUrl }} style={styles.socialPostThumbImage} />
                      ) : (
                        <View style={styles.socialPostThumbPlaceholder}>
                          <Icon name="image" size={22} color="#999" />
                        </View>
                      )}
                      {post.caption ? (
                        <Text style={styles.socialPostThumbCaption} numberOfLines={2}>{post.caption}</Text>
                      ) : null}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {hotspot.activityLevel && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Activity Level</Text>
              <View style={styles.activityCard}>
                <Icon name="trending-up" size={24} color="#FF9800" />
                <Text style={styles.activityText}>{hotspot.activityLevel}</Text>
              </View>
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Players Nearby</Text>
            {loadingPlayers ? (
              <ActivityIndicator size="small" color="#94EB98" style={styles.sectionLoader} />
            ) : nearbyPlayers.length > 0 ? (
              <View style={styles.playersGrid}>
                {nearbyPlayers.map((player) => (
                  <TouchableOpacity 
                    key={player.id} 
                    style={styles.playerCard}
                    onPress={() => navigation.navigate('UserDetail', { userId: player.id })}
                  >
                    {player.profilePictureUrl ? (
                      <Image source={{ uri: player.profilePictureUrl }} style={styles.playerAvatar} />
                    ) : (
                      <View style={styles.playerAvatarPlaceholder}>
                        <Icon name="person" size={20} color="#94EB98" />
                      </View>
                    )}
                    <Text style={styles.playerName} numberOfLines={1}>{player.username}</Text>
                    {player.isOnline && <View style={styles.onlineDot} />}
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <View style={styles.emptyStateHighlight}>
                <View style={styles.emptyStateIconCircle}>
                  <Icon name="group-add" size={40} color="#94EB98" />
                </View>
                <Text style={styles.emptyStateTitleHighlight}>Be the first to visit!</Text>
                <Text style={styles.emptyStateTextHighlight}>
                  Head to this location and show up on the map
                </Text>
              </View>
            )}
          </View>

          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Reviews</Text>
              {currentUserDbId && (
                <TouchableOpacity 
                  style={styles.writeReviewButton}
                  onPress={() => setShowWriteReview(true)}
                >
                  <Icon name="rate-review" size={18} color="#fff" />
                  <Text style={styles.writeReviewText}>Write Review</Text>
                </TouchableOpacity>
              )}
            </View>
            
            {loadingReviews ? (
              <ActivityIndicator size="small" color="#94EB98" style={styles.sectionLoader} />
            ) : reviews.length > 0 ? (
              <View style={styles.reviewsList}>
                {reviews.map((review) => (
                  <View key={review.id} style={styles.reviewCard}>
                    <View style={styles.reviewHeader}>
                      <TouchableOpacity 
                        style={styles.reviewerInfo}
                        onPress={() => review.user?.id && navigation.navigate('UserDetail', { userId: review.user.id })}
                      >
                        {review.user?.profilePictureUrl ? (
                          <Image source={{ uri: getProfilePictureUrl(review.user.profilePictureUrl, 'thumbnail') || review.user.profilePictureUrl }} style={styles.reviewerAvatar} />
                        ) : (
                          <View style={styles.reviewerAvatarPlaceholder}>
                            <Icon name="person" size={18} color="#94EB98" />
                          </View>
                        )}
                        <View>
                          <Text style={styles.reviewerName}>{review.user?.username || 'Anonymous'}</Text>
                          <Text style={styles.reviewDate}>{formatDate(review.createdAt)}</Text>
                        </View>
                      </TouchableOpacity>
                      <View style={styles.reviewRating}>{renderStars(review.rating)}</View>
                    </View>
                    {review.comment && <Text style={styles.reviewComment}>{review.comment}</Text>}
                  </View>
                ))}
              </View>
            ) : (
              <View style={styles.emptyStateHighlight}>
                <View style={styles.emptyStateIconCircle}>
                  <Icon name="star-outline" size={40} color="#FFD700" />
                </View>
                <Text style={styles.emptyStateTitleHighlight}>Be the first to review!</Text>
                <Text style={styles.emptyStateTextHighlight}>
                  Share your experience to help other chess players
                </Text>
                {currentUserDbId && (
                  <TouchableOpacity 
                    style={styles.emptyStateButton}
                    onPress={() => setShowWriteReview(true)}
                  >
                    <Icon name="rate-review" size={18} color="#fff" />
                    <Text style={styles.emptyStateButtonText}>Write a Review</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>

          <View style={styles.bottomSpacer} />
        </View>
      </ScrollView>

      <WriteReviewModal
        visible={showWriteReview}
        onClose={() => setShowWriteReview(false)}
        hotspotId={hotspot.id}
        hotspotName={hotspot.name}
        userId={currentUserDbId}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // ========== COMMON ==========
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorText: {
    fontSize: 18,
    color: '#999',
    marginTop: 16,
    marginBottom: 24,
  },
  backButtonError: {
    backgroundColor: '#94EB98',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  backButtonErrorText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  starsRow: {
    flexDirection: 'row',
  },
  starsContainer: {
    flexDirection: 'row',
  },

  // ========== DESKTOP STYLES ==========
  desktopBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  desktopLoadingCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 40,
  },
  desktopErrorCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 60,
    maxWidth: 400,
  },
  desktopModal: {
    backgroundColor: '#fff',
    borderRadius: 24,
    width: '100%',
    maxWidth: 720,
    maxHeight: '90vh',
    overflow: 'hidden',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.3,
    shadowRadius: 40,
    elevation: 24,
  },
  desktopCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  desktopScrollView: {
    flex: 1,
  },
  desktopHeader: {
    flexDirection: 'row',
    padding: 24,
    gap: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  desktopPhotoSection: {
    width: 240,
    height: 180,
  },
  desktopPhotoContainer: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  desktopPhoto: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  desktopPhotoNav: {
    position: 'absolute',
    bottom: 8,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  desktopPhotoNavBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopPhotoNavBtnDisabled: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  desktopPhotoCounter: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  desktopPhotoPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopPhotoPlaceholderIcon: {
    fontSize: 48,
    marginBottom: 4,
  },
  desktopPhotoPlaceholderText: {
    fontSize: 13,
    color: '#999',
  },
  desktopInfoSection: {
    flex: 1,
    paddingRight: 40,
  },
  desktopCategoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 12,
    marginBottom: 12,
  },
  desktopCategoryText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  desktopTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  desktopHotspotIcon: {
    fontSize: 26,
  },
  desktopHotspotName: {
    fontSize: 22,
    fontWeight: '700',
    color: '#333',
    flex: 1,
  },
  desktopRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  desktopRatingText: {
    fontSize: 13,
    color: '#666',
  },
  desktopLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  desktopCityText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  desktopAddressRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 8,
  },
  desktopAddressText: {
    fontSize: 13,
    color: '#888',
    flex: 1,
    lineHeight: 18,
  },
  desktopActivityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff8e1',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 10,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  desktopActivityText: {
    fontSize: 12,
    color: '#333',
    fontWeight: '500',
  },
  // Tags styles (desktop)
  desktopTagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  desktopTagBadge: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  desktopTagText: {
    color: '#555',
    fontSize: 11,
    fontWeight: '500',
  },
  // Social links styles (desktop)
  desktopSocialLinksRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
  },
  desktopSocialLink: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  desktopSocialLinkWithUrl: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 18,
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    flex: 1,
    minWidth: 0,
  },
  desktopWebsiteUrlText: {
    fontSize: 13,
    color: '#4CAF50',
    flex: 1,
    textDecorationLine: 'underline',
  },
  desktopSection: {
    padding: 24,
    paddingTop: 20,
    paddingBottom: 0,
  },
  desktopSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  desktopSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
  },
  desktopDescription: {
    fontSize: 14,
    color: '#555',
    lineHeight: 22,
  },
  desktopTwoColumn: {
    flexDirection: 'row',
    padding: 24,
    paddingTop: 20,
    gap: 24,
  },
  desktopColumnHalf: {
    flex: 1,
  },
  desktopScheduleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 14,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#94EB98',
  },
  desktopScheduleText: {
    fontSize: 13,
    color: '#333',
    flex: 1,
    lineHeight: 20,
  },
  desktopAmenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  desktopAmenityTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#e8f5e9',
    borderRadius: 14,
  },
  desktopAmenityText: {
    fontSize: 12,
    color: '#2E7D32',
    fontWeight: '500',
  },
  desktopEmptyState: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#fafafa',
    borderRadius: 12,
  },
  desktopEmptyTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#999',
    marginTop: 8,
  },
  desktopEmptyText: {
    fontSize: 11,
    color: '#bbb',
    marginTop: 2,
  },
  desktopPlayersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  desktopPlayerCard: {
    alignItems: 'center',
    width: 64,
    position: 'relative',
  },
  desktopPlayerAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginBottom: 4,
  },
  desktopPlayerAvatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  desktopPlayerName: {
    fontSize: 11,
    color: '#333',
    fontWeight: '500',
    textAlign: 'center',
  },
  desktopOnlineDot: {
    position: 'absolute',
    top: 0,
    right: 8,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#4CAF50',
    borderWidth: 2,
    borderColor: '#fff',
  },
  desktopEmptyHighlight: {
    alignItems: 'center',
    padding: 28,
    backgroundColor: '#f5fff5',
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#e8f5e9',
    borderStyle: 'dashed',
  },
  desktopEmptyIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  desktopEmptyHighlightTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#2E7D32',
    marginBottom: 4,
  },
  desktopEmptyHighlightText: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
  },
  desktopEmptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#94EB98',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginTop: 16,
  },
  desktopEmptyButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopWriteReviewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#94EB98',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 16,
  },
  desktopWriteReviewText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  desktopReviewsGrid: {
    gap: 12,
  },
  desktopReviewCard: {
    padding: 14,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
  },
  desktopReviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  desktopReviewerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  desktopReviewerAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  desktopReviewerAvatarPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopReviewerName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  desktopReviewDate: {
    fontSize: 11,
    color: '#999',
    marginTop: 1,
  },
  desktopReviewComment: {
    fontSize: 13,
    color: '#555',
    lineHeight: 19,
  },
  desktopBottomSpacer: {
    height: 24,
  },

  desktopSocialPostCard: {
    width: 160,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#fff',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  desktopSocialPostImage: {
    width: '100%',
    height: 200,
    resizeMode: 'cover',
  },
  desktopSocialPostPlaceholder: {
    width: 160,
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopSocialPostCaption: {
    fontSize: 11,
    color: '#555',
    paddingHorizontal: 10,
    paddingVertical: 8,
    lineHeight: 16,
  },

  // ========== MOBILE STYLES ==========
  scrollView: {
    flex: 1,
  },
  heroSection: {
    height: 280,
    backgroundColor: '#e0e0e0',
    position: 'relative',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  heroPlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
  },
  heroPlaceholderIcon: {
    fontSize: 64,
    marginBottom: 8,
  },
  heroPlaceholderText: {
    fontSize: 16,
    color: '#999',
  },
  photoIndicators: {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  photoIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.5)',
  },
  photoIndicatorActive: {
    backgroundColor: '#fff',
    width: 24,
  },
  photoNavigation: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    transform: [{ translateY: -20 }],
  },
  photoNavButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoNavButtonDisabled: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  backButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 60 : 16,
    left: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  categoryBadgeOverlay: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  categoryBadgeText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  content: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    marginTop: -24,
    paddingTop: 24,
    paddingHorizontal: 20,
  },
  headerSection: {
    marginBottom: 24,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  hotspotIcon: {
    fontSize: 32,
  },
  hotspotName: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  ratingSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  ratingText: {
    fontSize: 14,
    color: '#666',
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  cityText: {
    fontSize: 15,
    color: '#666',
    fontWeight: '500',
  },
  addressRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  addressText: {
    fontSize: 14,
    color: '#888',
    flex: 1,
    lineHeight: 20,
  },
  // Tags styles (mobile)
  mobileTagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  mobileTagBadge: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  mobileTagText: {
    color: '#555',
    fontSize: 12,
    fontWeight: '500',
  },
  // Social links styles (mobile)
  mobileSocialLinksRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  mobileSocialLink: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  mobileSocialLinkWithUrl: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 21,
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    flex: 1,
    minWidth: 0,
  },
  websiteUrlText: {
    fontSize: 14,
    color: '#4CAF50',
    flex: 1,
    textDecorationLine: 'underline',
  },
  section: {
    marginBottom: 28,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
  },
  description: {
    fontSize: 15,
    color: '#555',
    lineHeight: 24,
  },
  scheduleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#94EB98',
  },
  scheduleText: {
    fontSize: 15,
    color: '#333',
    flex: 1,
    lineHeight: 22,
  },
  amenitiesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  amenityTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#e8f5e9',
    borderRadius: 20,
  },
  amenityText: {
    fontSize: 13,
    color: '#2E7D32',
    fontWeight: '500',
  },
  socialPostThumb: {
    width: 160,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  socialPostThumbImage: {
    width: '100%',
    height: 200,
    resizeMode: 'cover',
  },
  socialPostThumbPlaceholder: {
    width: 160,
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  socialPostThumbCaption: {
    fontSize: 11,
    color: '#555',
    paddingHorizontal: 10,
    paddingVertical: 8,
    lineHeight: 16,
  },
  activityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: '#fff8e1',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#FF9800',
  },
  activityText: {
    fontSize: 15,
    color: '#333',
    fontWeight: '500',
  },
  playersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  playerCard: {
    alignItems: 'center',
    width: 72,
    position: 'relative',
  },
  playerAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    marginBottom: 6,
  },
  playerAvatarPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 6,
  },
  playerName: {
    fontSize: 12,
    color: '#333',
    fontWeight: '500',
    textAlign: 'center',
  },
  onlineDot: {
    position: 'absolute',
    top: 2,
    right: 8,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#4CAF50',
    borderWidth: 2,
    borderColor: '#fff',
  },
  writeReviewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#94EB98',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
  },
  writeReviewText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  reviewsList: {
    gap: 16,
  },
  reviewCard: {
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  reviewerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  reviewerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  reviewerAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  reviewerName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  reviewDate: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  reviewRating: {
    flexDirection: 'row',
  },
  reviewComment: {
    fontSize: 14,
    color: '#555',
    lineHeight: 21,
  },
  emptyState: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fafafa',
    borderRadius: 16,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#999',
    marginTop: 12,
  },
  emptyStateText: {
    fontSize: 13,
    color: '#bbb',
    marginTop: 4,
    textAlign: 'center',
  },
  emptyStateHighlight: {
    alignItems: 'center',
    padding: 32,
    backgroundColor: '#f5fff5',
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#e8f5e9',
    borderStyle: 'dashed',
  },
  emptyStateIconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e8f5e9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  emptyStateTitleHighlight: {
    fontSize: 18,
    fontWeight: '700',
    color: '#2E7D32',
    marginBottom: 8,
  },
  emptyStateTextHighlight: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 16,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 25,
    marginTop: 20,
  },
  emptyStateButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  sectionLoader: {
    padding: 20,
  },
  bottomSpacer: {
    height: 100,
  },
});

export default HotspotDetailScreen;
